import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { CrmService } from '../../services/crm.service';
import {PopupService} from "../../services/popup.service";
import * as CrmActions from './crm.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { selectReport } from '../search/search.selectors';
import { updateReport } from '../search/search.actions';
import { selectAuthUser } from '../auth/auth.selectors';

@Injectable()
export class CrmEffects {
  // Contact Effects
  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadContacts),
      withLatestFrom(this.store.select(selectAuthUser)),
      switchMap(([action, user]) =>
        this.crmService.getContacts({
          page: action.page,
          per_page: action.perPage,
          search: action.search,
          filters: action.filters,
          user_id: user?.id,
          sortBy: action.sortBy,
          sortDir: action.sortDir,
          dateRange: action.dateRange
        }).pipe(
          map((response) => {
            // console.log('CRM Service Response:', response);
            if (!response || !response.data) {
              throw new Error('Invalid contacts response');
            }
            return CrmActions.loadContactsSuccess({
              result: response.data,
              pagination: response.pagination || {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0
              }
            });
          }),
          catchError((error) => {
            console.error('Load contacts failed:', error);
            return of(CrmActions.loadContactsFailure({
              error: error.message || 'Failed to load contacts'
            }));
          })
        )
      )
    )
  );

  createContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createContact),
      switchMap((action) =>
        this.crmService.createContact(action.contact).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid create contact response');
            }
            return CrmActions.createContactSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Create contact failed:', error);
            return of(CrmActions.createContactFailure({
              error: error.message || 'Failed to create contact'
            }));
          })
        )
      )
    )
  );

  createContactSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createContactSuccess),
      switchMap(() => [
        CrmActions.loadContacts({}),
        CrmActions.loadContactsLookup()
      ])
    )
  );

  loadContactsLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadContactsLookup),
      withLatestFrom(this.store.select(selectAuthUser)),
      switchMap(([, user]) =>
        this.crmService.getContacts({
          page: 1,
          per_page: 10000,
          user_id: user?.id
        }).pipe(
          map((response) => CrmActions.loadContactsLookupSuccess({
            result: response?.data || []
          })),
          catchError((error) => {
            console.error('Load contacts lookup failed:', error);
            return of(CrmActions.loadContactsLookupFailure({
              error: error.message || 'Failed to load contacts lookup'
            }));
          })
        )
      )
    )
  );

  createCompanyContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createCompanyContact),
      switchMap((action) =>
        this.crmService.createCompanyContact({
          company_id: action.company_id,
          contact_id: action.contact_id,
          is_default: action.is_default
        }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid create company-contact response');
            }
            return CrmActions.createCompanyContactSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Create company-contact failed:', error);
            // Pass the full error object to preserve API response details
            return of(CrmActions.createCompanyContactFailure({
              error: error
            }));
          })
        )
      )
    )
  );

  createCompanyContactSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createCompanyContactSuccess),
      switchMap(() => [
        CrmActions.loadContacts({}),
        CrmActions.loadCompanies({}),
        CrmActions.loadContactsLookup(),
        CrmActions.loadCompaniesLookup()
      ])
    )
  );

  createCompanyContactFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createCompanyContactFailure),
      tap((action) => {
        // Extract error message from API response
        let errorMessage = 'Failed to add company to contact';
        
        if (action.error) {
          // Handle different error formats
          if (typeof action.error === 'string') {
            errorMessage = action.error;
          } else if (action.error.error) {
            // Handle Laravel validation errors
            if (typeof action.error.error === 'string') {
              errorMessage = action.error.error;
            } else if (action.error.error.message) {
              errorMessage = action.error.error.message;
            }
          } else if (action.error.message) {
            errorMessage = action.error.message;
          } else if (action.error.metadata && action.error.metadata.message) {
            errorMessage = action.error.metadata.message;
          } else if (action.error.status && action.error.statusText) {
            errorMessage = `${action.error.status}: ${action.error.statusText}`;
          }
        }
        
        this.popup.notify(errorMessage, 'Error', 'error');
      }),
      map(() => CrmActions.loadContacts({}))
    )
  );

  deleteCompanyContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteCompanyContact),
      switchMap((action) =>
        this.crmService.deleteCompanyContact(action.id).pipe(
          map(() => CrmActions.deleteCompanyContactSuccess({ id: action.id })),
          catchError((error) => {
            console.error('Delete company contact failed:', error);
            return of(CrmActions.deleteCompanyContactFailure({
              error: error.message || 'Failed to delete company-contact relationship'
            }));
          })
        )
      )
    )
  );

  deleteCompanyContactSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteCompanyContactSuccess),
      switchMap(() => [
        CrmActions.loadContacts({}),
        CrmActions.loadCompanies({}),
        CrmActions.loadContactsLookup(),
        CrmActions.loadCompaniesLookup()
      ])
    )
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateContact),
      switchMap((action) =>
        this.crmService.updateContact(action.id, action.contact).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid update contact response');
            }
            return CrmActions.updateContactSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Update contact failed:', error);
            return of(CrmActions.updateContactFailure({
              error: error.message || 'Failed to update contact'
            }));
          })
        )
      )
    )
  );

  updateContactSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateContactSuccess),
      switchMap(() => [
        CrmActions.loadContacts({}),
        CrmActions.loadContactsLookup()
      ])
    )
  );

  deleteContactSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteContactSuccess),
      map(() => CrmActions.loadContactsLookup())
    )
  );

  deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteContact),
      switchMap((action) =>
        this.crmService.deleteContact(action.id).pipe(
          map(() => CrmActions.deleteContactSuccess({ id: action.id })),
          catchError((error) => {
            console.error('Delete contact failed:', error);
            return of(CrmActions.deleteContactFailure({
              error: error.message || 'Failed to delete contact'
            }));
          })
        )
      )
    )
  );

  loadContactDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadContactDetail),
      switchMap((action) =>
        this.crmService.getContactDetail(action.id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid contact detail response');
            }
            return CrmActions.loadContactDetailSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Load contact detail failed:', error);
            return of(CrmActions.loadContactDetailFailure({
              error: error.message || 'Failed to load contact details'
            }));
          })
        )
      )
    )
  );

  // Company Effects
  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadCompanies),
      withLatestFrom(this.store.select(selectAuthUser)),
      switchMap(([action, user]) =>
        this.crmService.getCompanies({
          page: action.page,
          per_page: action.perPage,
          search: action.search,
          user_id: user?.id,
          sortBy: action.sortBy,
          sortDir: action.sortDir,
          dateRange: action.dateRange
        }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid companies response');
            }
            return CrmActions.loadCompaniesSuccess({
              result: response.data,
              pagination: response.pagination
            });
          }),
          catchError((error) => {
            console.error('Load companies failed:', error);
            return of(CrmActions.loadCompaniesFailure({
              error: error.message || 'Failed to load companies'
            }));
          })
        )
      )
    )
  );

  createCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createCompany),
      switchMap((action) =>
        this.crmService.createCompany(action.company).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid create company response');
            }
            return CrmActions.createCompanySuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Create company failed:', error);
            return of(CrmActions.createCompanyFailure({
              error: error.message || 'Failed to create company'
            }));
          })
        )
      )
    )
  );

  createCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createCompanySuccess),
      switchMap(() => [
        CrmActions.loadCompanies({}),
        CrmActions.loadCompaniesLookup()
      ])
    )
  );

  loadCompaniesLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadCompaniesLookup),
      withLatestFrom(this.store.select(selectAuthUser)),
      switchMap(([, user]) =>
        this.crmService.getCompanies({
          page: 1,
          per_page: 10000,
          user_id: user?.id
        }).pipe(
          map((response) => CrmActions.loadCompaniesLookupSuccess({
            result: response?.data || []
          })),
          catchError((error) => {
            console.error('Load companies lookup failed:', error);
            return of(CrmActions.loadCompaniesLookupFailure({
              error: error.message || 'Failed to load companies lookup'
            }));
          })
        )
      )
    )
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateCompany),
      switchMap((action) =>
        this.crmService.updateCompany(action.id, action.company).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid update company response');
            }
            return CrmActions.updateCompanySuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Update company failed:', error);
            return of(CrmActions.updateCompanyFailure({
              error: error.message || 'Failed to update company'
            }));
          })
        )
      )
    )
  );

  updateCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateCompanySuccess),
      switchMap(() => [
        CrmActions.loadCompanies({}),
        CrmActions.loadCompaniesLookup()
      ])
    )
  );

  deleteCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteCompany),
      switchMap((action) =>
        this.crmService.deleteCompany(action.id).pipe(
          map(() => CrmActions.deleteCompanySuccess({ id: action.id })),
          catchError((error) => {
            console.error('Delete company failed:', error);
            return of(CrmActions.deleteCompanyFailure({
              error: error.message || 'Failed to delete company'
            }));
          })
        )
      )
    )
  );

  deleteCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteCompanySuccess),
      switchMap(() => [
        CrmActions.loadCompanies({}),
        CrmActions.loadCompaniesLookup()
      ])
    )
  );

  loadCompanyDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadCompanyDetail),
      switchMap((action) =>
        this.crmService.getCompanyDetail(action.id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid company detail response');
            }
            return CrmActions.loadCompanyDetailSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Load company detail failed:', error);
            return of(CrmActions.loadCompanyDetailFailure({
              error: error.message || 'Failed to load company details'
            }));
          })
        )
      )
    )
  );

  // Deal Effects
  loadDeals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadDeals),
      withLatestFrom(this.store.select(selectAuthUser)),
      switchMap(([action, user]) =>
        this.crmService.getDeals({
          page: action.page,
          per_page: action.perPage,
          search: action.search,
          stage: action.stage,
          user_id: user?.id,
          sortBy: action.sortBy,
          sortDir: action.sortDir,
          dateRange: action.dateRange
        }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid deals response');
            }
            return CrmActions.loadDealsSuccess({
              result: response.data,
              pagination: response.pagination
            });
          }),
          catchError((error) => {
            console.error('Load deals failed:', error);
            return of(CrmActions.loadDealsFailure({
              error: error.message || 'Failed to load deals'
            }));
          })
        )
      )
    )
  );

  createDeal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createDeal),
      switchMap((action) =>
        this.crmService.createDeal(action.deal).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid create deal response');
            }
            return CrmActions.createDealSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Create deal failed:', error);
            return of(CrmActions.createDealFailure({ error }));
          })
        )
      )
    )
  );

  createDealSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createDealSuccess),
      map(() => CrmActions.loadDeals({}))
    )
  );

  createDealFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createDealFailure),
      tap((action) => {
        this.popup.notify(this.extractApiErrorMessage(action.error, 'Failed to create deal'), 'Error', 'error');
      })
    ),
    { dispatch: false }
  );

  updateDeal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateDeal),
      switchMap((action) =>
        this.crmService.updateDeal(action.id, action.deal).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid update deal response');
            }
            return CrmActions.updateDealSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Update deal failed:', error);
            return of(CrmActions.updateDealFailure({ error }));
          })
        )
      )
    )
  );

  updateDealSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateDealSuccess),
      map(() => CrmActions.loadDeals({}))
    )
  );

  updateDealFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateDealFailure),
      tap((action) => {
        this.popup.notify(this.extractApiErrorMessage(action.error, 'Failed to update deal'), 'Error', 'error');
      })
    ),
    { dispatch: false }
  );

  private extractApiErrorMessage(error: any, fallback: string): string {
    if (!error) return fallback;
    if (typeof error === 'string') return error;
    if (error.error?.metadata?.message) return error.error.metadata.message;
    if (error.error?.message) return error.error.message;
    if (error.message) return error.message;
    return fallback;
  }

  deleteDeal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteDeal),
      switchMap((action) =>
        this.crmService.deleteDeal(action.id).pipe(
          map(() => CrmActions.deleteDealSuccess({ id: action.id })),
          catchError((error) => {
            console.error('Delete deal failed:', error);
            return of(CrmActions.deleteDealFailure({
              error: error.message || 'Failed to delete deal'
            }));
          })
        )
      )
    )
  );

  deleteDealSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteDealSuccess),
      map(() => CrmActions.loadDeals({}))
    )
  );

  loadDealDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadDealDetail),
      switchMap((action) =>
        this.crmService.getDealDetail(action.id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid deal detail response');
            }
            return CrmActions.loadDealDetailSuccess({
              result: response.data
            });
          }),
          catchError((error) => {
            console.error('Load deal detail failed:', error);
            return of(CrmActions.loadDealDetailFailure({
              error: error.message || 'Failed to load deal details'
            }));
          })
        )
      )
    )
  );

  // Save Report to CRM Effects
  saveReportToCrm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.saveReportToCrm),
      switchMap((action) =>
        this.crmService.saveReportToCrm(action.searchId, action.config).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid save report to CRM response');
            }
            return CrmActions.saveReportToCrmSuccess({
              result: response
            });
          }),
          catchError((error) => {
            console.error('Save report to CRM failed:', error);
            return of(CrmActions.saveReportToCrmFailure({
              error: error.message || 'Failed to save report to CRM'
            }));
          })
        )
      )
    )
  );

  saveReportToCrmSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.saveReportToCrmSuccess),
      tap((action) => {
        const summary = action.result.metadata?.summary;
        const message = summary
          ? `Successfully saved to CRM! Processed ${summary.total_contacts_processed} contacts and ${summary.total_companies_processed} companies with ${summary.success_rate} success rate.`
          : 'Report successfully saved to CRM';

        this.popup.notify(message, 'Success', 'success');
      }),
      withLatestFrom(this.store.select(selectReport)),
      switchMap(([action, reportResponse]) => {
        const actions: any[] = [
          CrmActions.loadContacts({}),
          CrmActions.loadCompanies({})
        ];

        // Update report with CRM contact if available
        const createdContact = action.result?.data?.created_contacts?.[0];
        if (createdContact && reportResponse?.report) {
          const updatedReport = {
            ...reportResponse.report,
            crm_contact: createdContact,
            crm_contact_id: createdContact.id
          };
          actions.push(updateReport({ report: updatedReport }));
        }

        return actions;
      })
    )
  );

  saveReportToCrmFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.saveReportToCrmFailure),
      tap((action) => {
        let errorMessage = 'Failed to save report to CRM';

        if (action.error) {
          if (typeof action.error === 'string') {
            errorMessage = action.error;
          } else if (action.error.error?.message) {
            errorMessage = action.error.error.message;
          } else if (action.error.message) {
            errorMessage = action.error.message;
          }
        }

        this.popup.notify(errorMessage, 'Error', 'error');
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private crmService: CrmService,
    private popup: PopupService,
    private store: Store<AppState>
  ) {}
}
